/**
 * Mirrors the shared payment shell: fixed h-14 header, a max-w-[520px] column,
 * and the fixed pay bar. `showNotice` adds the reservation-countdown strip the
 * booking flows render above the summary — omit it for surfaces that have no
 * hold to count down, such as adding money.
 */
export function PaymentPageSkeleton({
  showNotice = false,
}: {
  showNotice?: boolean;
}) {
  return (
    // `data-slot="skeleton"` is what NavigationWatchdog looks for. This shell
    // hand-rolls its pulsing blocks rather than using the <Skeleton> primitive,
    // so without the marker a payment page stuck on its fallback would be
    // invisible to the watchdog — the one screen where that matters most.
    <div
      data-slot="skeleton"
      className="min-h-dvh bg-background pb-32 text-text-primary dark:bg-[#0b0b0f] dark:text-white"
    >
      <header className="fixed top-0 right-0 left-0 z-50 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur-md dark:border-glass-border-08 dark:bg-[#0b0b0f]/80">
        <div className="mx-auto flex h-14 max-w-[520px] items-center gap-3 px-4">
          <div className="h-9 w-9 animate-pulse rounded-full bg-surface-alt dark:bg-glass-bg-08" />
          <div className="h-5 w-44 animate-pulse rounded-full bg-surface-alt dark:bg-white/[0.1]" />
        </div>
      </header>
      <main className="mx-auto max-w-[520px] space-y-5 px-4 pt-[calc(env(safe-area-inset-top)+76px)]">
        {showNotice ? (
          <div className="h-11 animate-pulse rounded-[14px] bg-surface-alt dark:bg-glass-bg-07" />
        ) : null}
        <section className="space-y-4 rounded-[20px] border border-border bg-surface p-4 shadow-sm dark:border-white/[0.12] dark:bg-white/[0.055] dark:shadow-none">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 animate-pulse rounded-xl bg-surface-alt dark:bg-glass-bg-08" />
            <div className="flex-1 space-y-3">
              <div className="h-6 w-3/4 animate-pulse rounded bg-surface-alt dark:bg-white/[0.1]" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-surface-alt dark:bg-glass-bg-08" />
            </div>
          </div>
          <div className="h-px bg-border dark:bg-white/[0.1]" />
          <div className="h-16 animate-pulse rounded-2xl bg-surface-alt dark:bg-glass-bg-07" />
          <div className="h-16 animate-pulse rounded-2xl bg-surface-alt dark:bg-glass-bg-07" />
        </section>
      </main>
      <div className="fixed inset-x-0 bottom-0 z-20">
        <div className="mx-auto max-w-[520px] border-t border-border bg-surface px-4 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-[0_-12px_28px_rgba(15,23,42,0.08)] dark:border-white/[0.1] dark:bg-[#121116] dark:shadow-none">
          <div className="grid grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] gap-3">
            <div className="space-y-2 py-1">
              <div className="h-5 w-24 animate-pulse rounded bg-surface-alt dark:bg-glass-bg-08" />
              <div className="h-4 w-28 animate-pulse rounded bg-surface-alt dark:bg-white/[0.1]" />
            </div>
            <div className="h-[62px] animate-pulse rounded-[20px] bg-brand/25 dark:bg-white/90" />
          </div>
        </div>
      </div>
    </div>
  );
}
